import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { 
  Settings, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  ExternalLink,
  Trash2,
  FileSpreadsheet,
  Plus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleSheetsIntegration = () => {
  const [configs, setConfigs] = useState([]);
  const { tt } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [logs, setLogs] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    spreadsheet_id: '',
    sheet_name: '',
    range_name: 'Sheet1',
    auto_sync: true,
    sync_interval_minutes: 10
  });

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/sheets/config`);
      const loadedConfigs = response.data.configs || [];
      setConfigs(loadedConfigs);
      if (loadedConfigs.length > 0) {
        fetchLogs();
      }
    } catch (error) {
      console.error('Error fetching configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/sheets/logs?limit=50`);
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const handleAddConfig = async () => {
    try {
      if (!formData.spreadsheet_id) {
        alert(tt('Введите ссылку на таблицу или Spreadsheet ID'));
        return;
      }

      if (!formData.sheet_name.trim()) {
        alert(tt('Введите название таблицы'));
        return;
      }

      await axios.post(`${API}/sheets/config`, formData);
      alert(tt('Таблица добавлена успешно!'));
      setShowAddForm(false);
      setFormData({
        spreadsheet_id: '',
        sheet_name: '',
        range_name: 'Sheet1',
        auto_sync: true,
        sync_interval_minutes: 10
      });
      fetchConfigs();
      fetchLogs();
    } catch (error) {
      console.error('Error adding config:', error);
      alert('Ошибка добавления: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSync = async (configId) => {
    try {
      setSyncing(prev => ({ ...prev, [configId]: true }));
      const response = await axios.post(`${API}/sheets/sync/${configId}`);
      alert(`Синхронизация завершена!\nСоздано клиентов: ${response.data.clients_created}\nДубликатов: ${response.data.duplicates_found}`);
      fetchConfigs();
      fetchLogs();
    } catch (error) {
      console.error('Error syncing:', error);
      alert('Ошибка синхронизации: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSyncing(prev => ({ ...prev, [configId]: false }));
    }
  };

  const handleDelete = async (configId, sheetName) => {
    if (!window.confirm(`Вы уверены что хотите удалить таблицу "${sheetName}"?`)) return;
    
    try {
      await axios.delete(`${API}/sheets/config/${configId}`);
      alert(tt('Таблица удалена'));
      fetchConfigs();
      fetchLogs();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Ошибка удаления: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return <div className="text-center py-8">{tt('Загрузка...')}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <FileSpreadsheet className="w-6 h-6 mr-2 text-green-600" />
            {tt('Google Sheets интеграция')}
          </h2>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="btn btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            {tt('Добавить таблицу')}
          </button>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-blue-900 mb-2">{tt('✨ Простая настройка!')}</h4>
          <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
            <li>{tt('Откройте вашу Google Sheets таблицу')}</li>
            <li>{tt('Нажмите')} <strong>"Share"</strong> {tt('(Поделиться) →')} <strong>{tt('"Изменить"')}</strong></li>
            <li>{tt('Выберите')} <strong>{tt('"Все, у кого есть ссылка"')}</strong> {tt('→ роль')} <strong>{tt('"Читатель"')}</strong></li>
            <li>{tt('Скопируйте ссылку на таблицу')}</li>
            <li>{tt('Нажмите "Добавить таблицу" и вставьте ссылку')}</li>
          </ol>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-medium mb-4">{tt('Добавить новую таблицу')}</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {tt('Название таблицы *')}
                </label>
                <input
                  type="text"
                  value={formData.sheet_name}
                  onChange={(e) => setFormData({ ...formData, sheet_name: e.target.value })}
                  placeholder={tt('Например: Лиды из Facebook')}
                  className="input w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {tt('Удобное название для вас (не название листа в Google Sheets)')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {tt('Ссылка на таблицу или Spreadsheet ID *')}
                </label>
                <input
                  type="text"
                  value={formData.spreadsheet_id}
                  onChange={(e) => setFormData({ ...formData, spreadsheet_id: e.target.value })}
                  placeholder={tt('https://docs.google.com/spreadsheets/d/... или ID таблицы')}
                  className="input w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {tt('💡 Можно вставить полную ссылку - ID извлечется автоматически')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {tt('Название листа в таблице')}
                </label>
                <select
                  value={formData.range_name}
                  onChange={(e) => setFormData({ ...formData, range_name: e.target.value })}
                  className="input w-full"
                >
                  <option value="Sheet1">{tt('Sheet1 (первый лист)')}</option>
                  <option value="Sheet2">{tt('Sheet2 (второй лист)')}</option>
                  <option value="Sheet3">{tt('Sheet3 (третий лист)')}</option>
                  <option value="Лист1">{tt('Лист1 (первый лист)')}</option>
                  <option value="Лист2">{tt('Лист2 (второй лист)')}</option>
                </select>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="auto_sync"
                  checked={formData.auto_sync}
                  onChange={(e) => setFormData({ ...formData, auto_sync: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="auto_sync" className="text-sm font-medium text-gray-700">
                  {tt('Автоматическая синхронизация')}
                </label>
              </div>

              {formData.auto_sync && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {tt('Интервал синхронизации (минуты)')}
                  </label>
                  <select
                    value={formData.sync_interval_minutes}
                    onChange={(e) => setFormData({ ...formData, sync_interval_minutes: parseInt(e.target.value) })}
                    className="input w-full"
                  >
                    <option value="5">{tt('5 минут')}</option>
                    <option value="10">{tt('10 минут')}</option>
                    <option value="15">{tt('15 минут')}</option>
                    <option value="30">{tt('30 минут')}</option>
                    <option value="60">{tt('1 час')}</option>
                  </select>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={handleAddConfig}
                  className="btn btn-primary"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {tt('Добавить')}
                </button>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="btn btn-secondary"
                >
                  {tt('Отмена')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table Structure Info */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-green-900 mb-2">{tt('📋 Структура таблицы')}</h4>
          <p className="text-sm text-green-800 mb-2">
            {tt('Первая строка (заголовки):')}
          </p>
          <code className="text-xs bg-white px-2 py-1 rounded block mb-2">
            name | phone | email | describe | note
          </code>
          <ul className="text-sm text-green-800 space-y-1">
            <li>• <strong>name</strong> {tt('и')} <strong>phone</strong> {tt('- обязательные')}</li>
            <li>• <strong>email</strong>, <strong>describe</strong>, <strong>note</strong> {tt('- опциональные')}</li>
            <li>{tt('• Страна определяется автоматически по коду телефона')}</li>
            <li>{tt('• Кампания: "Гугл таблица"')}</li>
            <li>{tt('• Дубликаты (по email/phone) пропускаются автоматически')}</li>
          </ul>
        </div>

        {/* Configs List */}
        {configs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>{tt('Нет подключенных таблиц')}</p>
            <p className="text-sm">{tt('Нажмите "Добавить таблицу" чтобы начать')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Подключенные таблицы ({configs.length})</h3>
            {configs.map((config) => {
              const configLog = logs.find(log => log.config_id === config.id);
              
              return (
                <div key={config.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 text-lg">{config.sheet_name || 'Таблица'}</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        <strong>{tt('Таблица:')}</strong> {config.spreadsheet_id?.substring(0, 20)}...
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>{tt('Лист:')}</strong> {config.range_name}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>{tt('Автосинхронизация:')}</strong> {config.auto_sync ? `Включена (каждые ${config.sync_interval_minutes} мин)` : 'Выключена'}
                      </p>
                      {config.last_sync && (
                        <p className="text-sm text-gray-600">
                          <strong>{tt('Последняя синхронизация:')}</strong> {new Date(config.last_sync).toLocaleString('ru-RU')}
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleSync(config.id)}
                        disabled={syncing[config.id]}
                        className="btn btn-sm btn-secondary"
                      >
                        <RefreshCw className={`w-4 h-4 mr-1 ${syncing[config.id] ? 'animate-spin' : ''}`} />
                        {syncing[config.id] ? 'Синхронизация...' : 'Синхронизировать'}
                      </button>
                      <button
                        onClick={() => handleDelete(config.id, config.sheet_name)}
                        className="btn btn-sm bg-red-600 text-white hover:bg-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Latest Log for this config */}
                  {configLog && (
                    <div className={`border-t pt-3 mt-3 ${
                      configLog.status === 'success' ? 'border-green-200' :
                      configLog.status === 'error' ? 'border-red-200' :
                      'border-gray-200'
                    }`}>
                      <div className="flex items-center space-x-2 mb-1">
                        {configLog.status === 'success' && <CheckCircle className="w-4 h-4 text-green-600" />}
                        {configLog.status === 'error' && <XCircle className="w-4 h-4 text-red-600" />}
                        {configLog.status === 'running' && <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />}
                        <span className="text-sm font-medium">
                          {tt('Последняя синхронизация')}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600">
                        {new Date(configLog.started_at).toLocaleString('ru-RU')}
                      </p>
                      {configLog.status === 'success' && (
                        <p className="text-sm text-gray-700 mt-1">
                          {tt('Создано:')} <strong>{configLog.clients_created}</strong> {tt('| Дубликатов:')} <strong>{configLog.duplicates_found}</strong> {tt('| Обработано строк:')} <strong>{configLog.rows_processed}</strong>
                        </p>
                      )}
                      {configLog.error_message && (
                        <p className="text-sm text-red-600 mt-1">{configLog.error_message}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleSheetsIntegration;
